import type { FC, PropsWithChildren, RefObject } from "react"
import { useRef } from "react"

import { screenToStudio } from "@logic/lib/design"
import type { Node2d } from "@logic/lib/mesh"
import type { DraggableEvent } from "react-draggable"
import { DraggableCore } from "react-draggable"
import { useRecoilState, useRecoilValue } from "recoil"

import { activityState } from "../../state/activity"
import { useFinishMove, useStartMove } from "../../state/elements/transactions"
import type { Tool } from "../../state/tool"
import { toolState } from "../../state/tool"
import { zoomState } from "../../state/view"

type DragProps = PropsWithChildren<{
  position: Node2d
  onDrag: ((position: Node2d) => void) | null
  onStop?: () => void
  nodeRef: RefObject<HTMLElement>
  onMouseDown?: (e: MouseEvent) => void
  tool: Tool
}>

export const Drag: FC<DragProps> = ({
  tool,
  children,
  onDrag,
  nodeRef,
  onMouseDown,
  onStop,
}) => {
  const [activity, setActivity] = useRecoilState(activityState)
  const zoom = useRecoilValue(zoomState)
  const currentTool = useRecoilValue(toolState)
  const canDrag = [null, `dragging`].includes(activity) && onDrag !== null
  const displacement = useRef<Node2d>({ x: 0, y: 0 })
  const startMove = useStartMove()
  const finishMove = useFinishMove()

  const updatePosition = (e: DraggableEvent) => {
    if (canDrag && e instanceof MouseEvent) {
      const { movementX, movementY } = e
      const hasMoved = movementX || movementY
      if (hasMoved) {
        displacement.current.y += movementY
        displacement.current.x += movementX
        if (!activity) setActivity(`dragging`)
      }
      const move = {
        x: movementX,
        y: movementY,
      }
      const scaledMove = screenToStudio.point(move, zoom)
      onDrag(scaledMove)
    }
  }

  return onDrag ? (
    <DraggableCore
      onMouseDown={onMouseDown}
      nodeRef={nodeRef}
      onDrag={tool === currentTool ? updatePosition : undefined}
      onStart={startMove}
      onStop={() => {
        onStop?.()
        const wasDisplaced = displacement.current.x || displacement.current.y
        if (wasDisplaced) {
          finishMove()
          setActivity(null)
        }
        displacement.current.x = 0
        displacement.current.y = 0
      }}
    >
      {children}
    </DraggableCore>
  ) : (
    <>{children}</>
  )
}
